'use client';

import {useState} from 'react';
import {usePathname} from 'next/navigation';
import {createPortal} from 'react-dom';
import {ExternalLink, Mic} from 'lucide-react';
import {useAuth} from 'iq-auth';
import {AuthForm} from 'iq-auth/components/auth_form';
import {getUser} from 'common/users';
import {trackDiaformOpened} from 'iq-interview_experiences/tracking';
import {API} from 'common/api';

const DIAFORM_URL = process.env.NEXT_PUBLIC_DIAFORM_URL ?? '';

function DiaformConfirmModal({onClose, onConfirm}: {onClose: () => void; onConfirm: () => void}) {
  return createPortal(
    <div
      className='fixed inset-0 z-1100 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm'
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className='bg-white rounded-xl max-w-md w-full p-8 shadow-lg'>
        <h2 className='text-xl font-bold text-iq-black mb-3 leading-snug'>Submit via Diaform</h2>
        <p className='text-base text-iq-grey-dark mb-2 leading-relaxed'>
          An AI voice assistant will guide you through a few questions about your recent interview.
          Takes about 5 minutes.
        </p>
        <p className='text-sm text-iq-grey-medium mb-8 leading-relaxed'>
          The AI will ask for your email at the start —{' '}
          <strong>make sure to say it clearly and exactly as you used to sign up</strong>. This is
          how we link your voice submission to your account and unlock your benefits.
        </p>
        <div className='flex items-center justify-between'>
          <button
            type='button'
            onClick={onClose}
            className='text-sm text-iq-grey-medium hover:text-iq-black transition-colors'>
            Cancel
          </button>
          <a
            href={DIAFORM_URL}
            target='_blank'
            rel='noopener noreferrer'
            onClick={onConfirm}
            className='flex items-center gap-2 px-6 py-3 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity'>
            Continue to Diaform
            <ExternalLink className='w-3.5 h-3.5' />
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DiaformAuthModal({
  onSuccess,
  onClose,
  next,
}: {
  onSuccess: () => void;
  onClose: () => void;
  next?: string;
}) {
  return createPortal(
    <div
      className='fixed inset-0 z-1100 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm'
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className='bg-white rounded-xl max-w-md w-full p-8 shadow-lg'>
        <AuthForm
          onSuccess={onSuccess}
          onBack={onClose}
          cta='Sign up or log in to submit your experience via Diaform.'
          next={next}
        />
      </div>
    </div>,
    document.body,
  );
}

type FlowDiaformCtaProps = {
  submissionId: string;
  source: string;
  guideSlug?: string;
};

export function FlowDiaformCta({submissionId, source, guideSlug}: FlowDiaformCtaProps) {
  const {user, setUser} = useAuth();
  const pathname = usePathname();
  const [modalState, setModalState] = useState<'auth' | 'confirm' | null>(null);

  function recordIntent() {
    void API.post('/v1/interview-experiences/diaform-opened', {
      submission_flow_id: submissionId,
      source,
      guide_slug: guideSlug ?? null,
    });
  }

  function handleClick() {
    void trackDiaformOpened(submissionId);
    if (user.loggedIn) {
      setModalState('confirm');
    } else {
      setModalState('auth');
    }
  }

  async function handleAuthSuccess() {
    const freshUser = await getUser();
    setUser(freshUser);
    setModalState('confirm');
  }

  return (
    <>
      <div
        className='flex items-center justify-between rounded-lg px-4 py-3 mb-6'
        style={{background: 'linear-gradient(to right, #262c45, #66497e, #8c4a66)'}}>
        <div className='flex items-center gap-2'>
          <Mic className='w-4 h-4 text-white/60' />
          <p className='text-sm text-white/80'>Prefer to share via voice?</p>
        </div>
        <button
          type='button'
          onClick={handleClick}
          className='flex items-center gap-1.5 text-sm font-semibold text-white hover:opacity-70 transition-opacity'>
          Submit via Diaform
          <ExternalLink className='w-3.5 h-3.5' />
        </button>
      </div>
      {modalState === 'auth' && (
        <DiaformAuthModal
          onSuccess={() => void handleAuthSuccess()}
          onClose={() => setModalState(null)}
          next={pathname}
        />
      )}
      {modalState === 'confirm' && <DiaformConfirmModal onClose={() => setModalState(null)} onConfirm={recordIntent} />}
    </>
  );
}
